import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tabs,
    Tab,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const SECTION_DEFS = [
    { key: 'progressSummary', title: 'Progress Summary', prompt: 'What progress has the student made?' },
    { key: 'areasToImprove', title: 'What I Need to Improve', prompt: 'What areas do I need to improve in?' },
];

const STATUS_COLOR = { draft: 'default', submitted: 'info', adminEdited: 'warning', sent: 'success' };

const studentName = (s) => {
    if (!s) return '—';
    if (s.role === 'parent' && s.childInfo?.studentName) return s.childInfo.studentName;
    return `${s.firstName || ''} ${s.surname || ''}`.trim();
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export default function ReportsPage() {
    const confirm = useConfirm();
    const [tab, setTab] = useState('reports');
    const [years, setYears] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [academicYearId, setAcademicYearId] = useState('');
    const [periodId, setPeriodId] = useState('');

    // Reporting period dialog
    const [periodDialog, setPeriodDialog] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState(null);
    const [periodName, setPeriodName] = useState('End of Term 2');
    const [periodSendBy, setPeriodSendBy] = useState('');

    // Report edit dialog
    const [editingReport, setEditingReport] = useState(null);
    const [sections, setSections] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/api/admin/academic-years`, authHeaders())
            .then((r) => {
                setYears(r.data || []);
                const active = (r.data || []).find((y) => y.isActive);
                if (active) setAcademicYearId(active._id);
            })
            .catch(() => {});
    }, []);

    const loadPeriods = async (yearId) => {
        const params = yearId ? `?academicYearId=${yearId}` : '';
        const { data } = await axios.get(`${API_URL}/api/admin/reporting-periods${params}`, authHeaders());
        setPeriods(data || []);
        // Default to most-recent open period.
        if (data?.length && !periodId) {
            const open = data.find((p) => !p.closed) || data[0];
            setPeriodId(open._id);
        }
    };

    useEffect(() => {
        if (academicYearId) loadPeriods(academicYearId);
    }, [academicYearId]);

    const loadReports = async () => {
        if (!periodId) { setReports([]); return; }
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/reports?periodId=${periodId}`, authHeaders());
            setReports(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadReports(); /* eslint-disable-next-line */ }, [periodId]);

    /* ----- reporting period CRUD ----- */

    const openCreatePeriod = () => {
        setEditingPeriod(null);
        setPeriodName('End of Term 2');
        setPeriodSendBy('');
        setPeriodDialog(true);
    };
    const openEditPeriod = (p) => {
        setEditingPeriod(p);
        setPeriodName(p.name || '');
        setPeriodSendBy(p.sendByDate ? p.sendByDate.slice(0, 10) : '');
        setPeriodDialog(true);
    };
    const savePeriod = async () => {
        try {
            if (editingPeriod) {
                await axios.patch(
                    `${API_URL}/api/admin/reporting-periods/${editingPeriod._id}`,
                    { name: periodName.trim(), sendByDate: periodSendBy || null },
                    authHeaders()
                );
            } else {
                await axios.post(
                    `${API_URL}/api/admin/reporting-periods`,
                    { academicYearId, name: periodName.trim(), sendByDate: periodSendBy || null },
                    authHeaders()
                );
            }
            setPeriodDialog(false);
            await loadPeriods(academicYearId);
            setMessage('Reporting period saved.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const removePeriod = async (id) => {
        if (!(await confirm({ title: 'Delete reporting period', message: 'Delete this reporting period?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/reporting-periods/${id}`, authHeaders());
            await loadPeriods(academicYearId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const togglePeriodClosed = async (p) => {
        await axios.patch(
            `${API_URL}/api/admin/reporting-periods/${p._id}`,
            { closed: !p.closed },
            authHeaders()
        );
        await loadPeriods(academicYearId);
    };

    /* ----- report editing + sending ----- */

    const openEditReport = (r) => {
        setEditingReport(r);
        setSections({
            progressSummary: r.sections?.progressSummary || '',
            areasToImprove: r.sections?.areasToImprove || '',
            strengths: r.sections?.strengths || '',
            nextSteps: r.sections?.nextSteps || '',
            tutorComments: r.sections?.tutorComments || '',
        });
    };
    const saveReport = async () => {
        setSaving(true);
        try {
            await axios.patch(
                `${API_URL}/api/admin/reports/${editingReport._id}`,
                { sections },
                authHeaders()
            );
            setEditingReport(null);
            await loadReports();
            setMessage('Report saved.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setSaving(false);
        }
    };
    const sendReport = async (id) => {
        if (!(await confirm({ title: 'Send report', message: 'Send this progress report by email?', confirmLabel: 'Send' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/reports/${id}/send`, {}, authHeaders());
            await loadReports();
            setMessage('Report sent.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send.');
        }
    };
    const previewReport = async (id) => {
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/reports/${id}/preview-pdf`,
                { ...authHeaders(), responseType: 'blob' }
            );
            const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(blobUrl, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load preview.');
        }
    };
    const bulkSend = async () => {
        if (!periodId) return;
        if (!(await confirm({ title: 'Send all reports', message: 'Send all draft / submitted reports for this reporting period?', confirmLabel: 'Send all' }))) return;
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/reports/bulk-send`,
                { periodId },
                authHeaders()
            );
            await loadReports();
            setMessage(`Sent ${data.sent} of ${data.requested}.`);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const counts = useMemo(() => {
        const out = { draft: 0, submitted: 0, adminEdited: 0, sent: 0 };
        for (const r of reports) out[r.status] = (out[r.status] || 0) + 1;
        return out;
    }, [reports]);

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Progress reports</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Tutors draft reports per student per reporting period. You review, optionally
                        edit, then send — the PDF is generated and emailed to the parent (and student,
                        when there's a separate student account linked to the parent).
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>The two tabs</strong> on this page do different things — neither is
                            "old" or "current" reports; they cover different concepts:
                            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                <li>
                                    <strong>Reports</strong> — the list of actual per-student reports
                                    inside a chosen reporting period. This is where you <em>review, edit,
                                    and send</em> individual reports. Filter by period at the top of the
                                    tab. A row shows one student × one class × one period, with a
                                    status chip (<em>Not started / Draft / Submitted / Edited by admin /
                                    Sent</em>) and actions per row. This is the day-to-day workflow.
                                </li>
                                <li>
                                    <strong>Reporting periods</strong> — the <em>windows</em> themselves.
                                    Here you create a period (e.g. <em>End of Term 1</em>), set its
                                    <em> Send by date</em>, and open or close it. You don't write or
                                    send reports on this tab; you only manage the windows tutors write
                                    against.
                                </li>
                            </ul>
                            Think of <strong>Reporting periods</strong> as "set up the deadlines" and
                            <strong> Reports</strong> as "do the review work". You'll spend almost all
                            your time on the <em>Reports</em> tab; the <em>Reporting periods</em> tab is
                            mostly a start-of-term / end-of-term action.
                            <br /><br />
                            <strong>Reporting periods</strong> are the "reporting windows" you open for
                            each academic year (e.g. <em>End of Term 1</em>, <em>End of Term 2</em>).
                            Create them on the <em>Reporting periods</em> tab. Each period has an optional
                            <em> Send by date</em> — the deadline tutors see on their portal so they
                            know when to submit drafts by. It's a nudge, not a hard block.<br /><br />
                            <strong>Open vs Closed:</strong> a period is <em>Open</em> by default while
                            tutors are actively writing and admin is reviewing / sending. When the batch
                            is done (every report has gone out, no more edits expected), click the green{' '}
                            <strong>Open</strong> chip on the period's row to toggle it to grey{' '}
                            <strong>Closed</strong>. Closing is <strong>manual</strong> — nothing auto-closes
                            based on the send-by date.<br /><br />
                            <strong>What Closed does:</strong>
                            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                <li>Tutors can no longer save new drafts or edit existing ones for that period.</li>
                                <li>The dropdown on the tutor portal appends <em>(closed)</em> to the period name so they know why.</li>
                                <li>Reports already <em>sent</em> stay readable — closing doesn't hide anything.</li>
                                <li>Admin can re-open a closed period any time by clicking the chip again.</li>
                            </ul>
                            <br />
                            <strong>Report lifecycle</strong> (per student per class):
                            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                <li><em>Not started</em> → tutor hasn't opened it yet.</li>
                                <li><em>Draft</em> → tutor has saved but not submitted.</li>
                                <li><em>Submitted</em> → tutor pressed <strong>Submit to admin</strong>; admin can now review and edit.</li>
                                <li><em>Edited by admin</em> → admin changed the copy.</li>
                                <li><em>Sent</em> → admin generated the PDF and emailed it. Tutor + admin can still read the sent report; nothing more can be edited.</li>
                            </ul>
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Tab value="reports" label="Reports (review & send)" />
                <Tab value="periods" label="Reporting periods (windows)" />
            </Tabs>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                {tab === 'reports'
                    ? 'Reports written by tutors for each student in a reporting period. Filter by period above. Review, edit and send from here.'
                    : 'The reporting "windows" themselves. Create a period (e.g. End of Term 1), set its Send by date, open it while tutors are writing, close it when the batch is done.'}
            </Typography>

            {tab === 'periods' && (
                <Card>
                    <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                        <TextField
                            select size="small" label="Academic year"
                            value={academicYearId}
                            onChange={(e) => setAcademicYearId(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y._id} value={y._id}>{y.label}</MenuItem>
                            ))}
                        </TextField>
                        <Button onClick={openCreatePeriod} variant="contained">New period</Button>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Send by</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {periods.map((p) => (
                                <TableRow key={p._id}>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell>{fmtDate(p.sendByDate)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={p.closed ? 'Closed' : 'Open'}
                                            color={p.closed ? 'default' : 'success'}
                                            onClick={() => togglePeriodClosed(p)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEditPeriod(p)}><EditIcon /></IconButton>
                                        <IconButton onClick={() => removePeriod(p._id)}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {periods.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No reporting periods yet — create one to allow tutors to start drafting.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {tab === 'reports' && (
                <>
                    <Card sx={{ p: 2, mb: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                select size="small" label="Academic year"
                                value={academicYearId}
                                onChange={(e) => { setAcademicYearId(e.target.value); setPeriodId(''); }}
                                sx={{ minWidth: 200 }}
                            >
                                {years.map((y) => (
                                    <MenuItem key={y._id} value={y._id}>{y.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select size="small" label="Reporting period"
                                value={periodId}
                                onChange={(e) => setPeriodId(e.target.value)}
                                sx={{ minWidth: 240 }}
                                disabled={!periods.length}
                            >
                                {periods.map((p) => (
                                    <MenuItem key={p._id} value={p._id}>
                                        {p.name}{p.closed ? ' (closed)' : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button onClick={bulkSend} variant="contained" disabled={!periodId || counts.draft + counts.submitted + counts.adminEdited === 0}>
                                Bulk send ({counts.draft + counts.submitted + counts.adminEdited})
                            </Button>
                        </Stack>
                    </Card>

                    <Card>
                        {loading ? (
                            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Student</TableCell>
                                        <TableCell>Class</TableCell>
                                        <TableCell>Tutor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Sent</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports.map((r) => (
                                        <TableRow key={r._id}>
                                            <TableCell>{studentName(r.studentId)}</TableCell>
                                            <TableCell>{r.tutoringClassId?.name || '—'}</TableCell>
                                            <TableCell>
                                                {r.tutorId
                                                    ? `${r.tutorId.firstName || ''} ${r.tutorId.surname || ''}`.trim()
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} />
                                            </TableCell>
                                            <TableCell>{fmtDate(r.sentAt)}</TableCell>
                                            <TableCell align="right">
                                                <IconButton onClick={() => openEditReport(r)} title="Edit"><EditIcon /></IconButton>
                                                <IconButton onClick={() => previewReport(r._id)} title="Preview PDF"><VisibilityIcon /></IconButton>
                                                {r.status !== 'sent' && (
                                                    <IconButton onClick={() => sendReport(r._id)} title="Send by email"><SendIcon /></IconButton>
                                                )}
                                                {r.pdfUrl && (
                                                    <Button size="small" component="a" href={r.pdfUrl} target="_blank" rel="noreferrer">PDF</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {reports.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                    No reports yet for this period. Tutors create them from their portal.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </>
            )}

            {/* Reporting period dialog */}
            <Dialog open={periodDialog} onClose={() => setPeriodDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPeriod ? 'Edit reporting period' : 'New reporting period'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Name"
                            value={periodName}
                            onChange={(e) => setPeriodName(e.target.value)}
                            size="small"
                            helperText='e.g. "End of Term 2" or "End of Term 3"'
                        />
                        <TextField
                            label="Send by"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={periodSendBy}
                            onChange={(e) => setPeriodSendBy(e.target.value)}
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPeriodDialog(false)}>Cancel</Button>
                    <Button onClick={savePeriod} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Report edit dialog */}
            <Dialog open={!!editingReport} onClose={() => setEditingReport(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingReport ? (
                        <>
                            {studentName(editingReport.studentId)} · {editingReport.tutoringClassId?.name || ''}
                            {editingReport.status === 'sent' && (
                                <Chip size="small" label="Sent" color="success" sx={{ ml: 2 }} />
                            )}
                        </>
                    ) : 'Report'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {SECTION_DEFS.map((sec) => (
                            <Box key={sec.key}>
                                <Typography sx={{ fontWeight: 600 }}>{sec.title}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {sec.prompt}
                                </Typography>
                                <TextField
                                    value={sections[sec.key] || ''}
                                    onChange={(e) => setSections((s) => ({ ...s, [sec.key]: e.target.value }))}
                                    multiline minRows={3}
                                    fullWidth size="small"
                                />
                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingReport(null)} disabled={saving}>Cancel</Button>
                    {editingReport && (
                        <Button
                            onClick={async () => {
                                const id = editingReport._id;
                                await saveReport();
                                previewReport(id);
                            }}
                            disabled={saving}
                            startIcon={<VisibilityIcon />}
                        >
                            Save & preview
                        </Button>
                    )}
                    <Button onClick={saveReport} variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
